import { Component, OnInit, ViewChild, TemplateRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { Subject, merge, of as observableOf } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CoreService } from '../../../services/core.service';
import { ManageApplicationNavComponent } from '../manage-application-nav/manage-application-nav.component';
import { SocialAiSiteFormComponent } from '../../../components/forms/create-account/site-form/site-form.component';
import { DialogComponent } from '../../../components/dialog/dialog.component';

@Component({
  selector: 'app-index-sites',
  standalone: true,
  imports: [
    CommonModule,
    MatPaginatorModule,
    MatTableModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatSortModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatDialogModule,
    ManageApplicationNavComponent,
    SocialAiSiteFormComponent
  ],
  templateUrl: './index-sites.component.html',
  styleUrl: './index-sites.component.scss'
})
export class ManageApplicationIndexSitesComponent implements OnInit, OnDestroy {
  @ViewChild('siteFormTmpl', { static: true }) siteFormTmpl!: TemplateRef<any>;
  @ViewChild('confirmDeleteTmpl', { static: true }) confirmDeleteTmpl!: TemplateRef<any>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  dataSource = new MatTableDataSource<any>([]);
  displayedColumns: string[] = ['id', 'thumbnail', 'url', 'cms', 'status', 'created_at', 'total_published_posts', 'actions'];

  searchValue: string = "";
  totalItems = 0;
  pageSize = 12;
  currentPage = 1;
  isLoading = true;
  hasLoaded = false;
  selectedSite: any = {};

  sortField: string = 'id';
  sortDirection: string = 'desc';

  private destroy$ = new Subject<void>();

  constructor(
    private coreService: CoreService,
    public dialog: MatDialog
  ) { }

  ngOnInit() {
    this.loadSites();
  }

  loadSites(page: number = 1) {
    this.isLoading = true;

    this.coreService
      .getData('/manageAccount/all-sites/index', {
        page,
        keyword: this.searchValue,
        sort: this.sortField,
        direction: this.sortDirection,
        per_page: this.pageSize
      })
      .subscribe({
        next: (res: any) => {
          this.dataSource.data = res.data || [];
          this.totalItems = res.total || 0;
          this.pageSize = res.per_page || 12;
          this.currentPage = res.current_page || page;
          this.hasLoaded = true;

          if (this.paginator) {
            this.paginator.pageIndex = this.currentPage - 1;
            this.paginator.length = this.totalItems;
            this.paginator.pageSize = this.pageSize;
          }
        },
        error: (err) => {
          console.error('Load sites error:', err);
          this.dataSource.data = [];
          this.hasLoaded = true;
          this.isLoading = false;
        },
        complete: () => {
          this.isLoading = false;
        }
      });
  }

  onSearchSubmit(value: string) {
    this.searchValue = value.trim();
    this.currentPage = 1;

    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }

    this.loadSites(1);
  }

  clearSearch() {
    this.searchValue = "";
    this.currentPage = 1;

    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }

    this.loadSites(1);
  }

  onPageChange(event: any) {
    const newPage = event.pageIndex + 1;
    this.currentPage = newPage;
    this.loadSites(newPage);
  }

  onSortChange(sort: Sort) {
    this.sortField = sort.active;
    this.sortDirection = sort.direction || 'desc';
    this.currentPage = 1;

    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }

    this.loadSites(1);
  }

  addNewSite() {
    this.selectedSite = {};
    this.dialog.open(DialogComponent, {
      data: {
        content: this.siteFormTmpl,
        header: 'Add Content Site',
        cls: '!bg-slate-600 !text-white',
      },
      width: '760px',
      maxWidth: '760px',
    });
  }

  editSite(row: any) {
    this.selectedSite = {
      ...row,
      id: row.ID || row.id
    };

    if ('ID' in this.selectedSite) {
      delete this.selectedSite.ID;
    }

    this.dialog.open(DialogComponent, {
      data: {
        content: this.siteFormTmpl,
        header: 'Edit Site',
        cls: '!bg-slate-600 !text-white',
      },
      width: '760px',
      maxWidth: '760px',
    });
  }

  openDeleteConfirm(row: any) {
    this.selectedSite = row;
    this.dialog.open(DialogComponent, {
      data: {
        content: this.confirmDeleteTmpl,
        header: 'Delete Site?',
        cls: 'bg-red-500 !text-white',
      },
    });
  }

  deleteSite() {
    const siteIdToDelete = this.selectedSite.ID || this.selectedSite.id;
    if (!siteIdToDelete) {
      this.coreService.showSnackbar("Site ID is missing for deletion.");
      return;
    }

    this.coreService
      .deleteData("deleteSite", String(siteIdToDelete))
      .subscribe({
        next: () => {
          this.dialog.closeAll();
          localStorage.removeItem("sites");
          this.coreService.showSnackbar('Site deleted successfully');
          this.loadSites(this.currentPage);
        },
        error: (err) => {
          console.error('Delete site error:', err);
          this.dialog.closeAll();
          this.coreService.showSnackbar('Failed to delete site');
        }
      });
  }

  onSubmitSiteClbk() {
    this.dialog.closeAll();
    this.loadSites(this.currentPage);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}